import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Course } from './MobileAdminCourses';

interface Hole {
  id: string;
  course_id: string;
  hole_number: number;
  par: number;
  handicap_index: number | null;
  distance_meters: number | null;
  distance_yards: number | null;
  description: string | null;
  notes: string | null;
}

interface TeeBox {
  id: string;
  course_id: string;
  name: string;
  color: string;
  player_categories: string[];
  course_rating: number | null;
  slope_rating: number | null;
  sort_order: number;
}

interface HoleTeeDistance {
  id: string;
  hole_id: string;
  tee_box_id: string;
  distance_yards: number | null;
  distance_meters: number | null;
}

interface HoleForm {
  par: number;
  handicap_index: string;
  distance_meters: string;
  distance_yards: string;
  description: string;
  notes: string;
}

interface TeeDistanceForm {
  [teeBoxId: string]: { yards: string; meters: string };
}

interface TeeBoxForm {
  name: string;
  color: string;
  player_categories: string;
  course_rating: string;
  slope_rating: string;
}

const TEE_COLORS = [
  { value: 'black', label: 'Black', bg: 'bg-gray-900' },
  { value: 'gold', label: 'Gold', bg: 'bg-yellow-500' },
  { value: 'blue', label: 'Blue', bg: 'bg-blue-600' },
  { value: 'white', label: 'White', bg: 'bg-white border border-gray-300' },
  { value: 'red', label: 'Red', bg: 'bg-red-500' },
  { value: 'green', label: 'Green', bg: 'bg-green-600' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-300' },
  { value: 'orange', label: 'Orange', bg: 'bg-orange-500' },
];

const COLOR_DOT: Record<string, string> = {
  black: 'bg-gray-900', gold: 'bg-yellow-500', blue: 'bg-blue-600',
  white: 'bg-white border border-gray-300', red: 'bg-red-500',
  green: 'bg-green-600', yellow: 'bg-yellow-300', orange: 'bg-orange-500',
};

const parColors: Record<number, string> = {
  3: 'bg-blue-100 text-blue-700',
  4: 'bg-green-100 text-green-700',
  5: 'bg-amber-100 text-amber-700',
  6: 'bg-red-100 text-red-700',
};

function defaultHoleForm(): HoleForm {
  return { par: 4, handicap_index: '', distance_meters: '', distance_yards: '', description: '', notes: '' };
}

function defaultTeeForm(): TeeBoxForm {
  return { name: '', color: 'white', player_categories: '', course_rating: '', slope_rating: '' };
}

interface Props {
  course: Course;
  onBack: () => void;
}

type EditorTab = 'holes' | 'tees';

export default function MobileAdminHoleEditor({ course, onBack }: Props) {
  const [activeTab, setActiveTab] = useState<EditorTab>('holes');
  const [holes, setHoles] = useState<Hole[]>([]);
  const [teeBoxes, setTeeBoxes] = useState<TeeBox[]>([]);
  const [teeDistances, setTeeDistances] = useState<HoleTeeDistance[]>([]);
  const [selectedHole, setSelectedHole] = useState(1);
  const [form, setForm] = useState<HoleForm>(defaultHoleForm());
  const [teeForm, setTeeForm] = useState<TeeDistanceForm>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [teeSheetOpen, setTeeSheetOpen] = useState(false);
  const [editingTee, setEditingTee] = useState<TeeBox | null>(null);
  const [teeBoxForm, setTeeBoxForm] = useState<TeeBoxForm>(defaultTeeForm());
  const [savingTee, setSavingTee] = useState(false);
  const [deletingTeeId, setDeletingTeeId] = useState<string | null>(null);

  const totalHoles = course.holes_count;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [holesRes, teesRes] = await Promise.all([
        supabase.from('holes').select('*').eq('course_id', course.id).order('hole_number'),
        supabase.from('tee_boxes').select('*').eq('course_id', course.id).order('sort_order'),
      ]);
      if (holesRes.error) throw holesRes.error;
      const loadedHoles = (holesRes.data as Hole[]) ?? [];
      const loadedTees = (teesRes.data as TeeBox[]) ?? [];
      setHoles(loadedHoles);
      setTeeBoxes(loadedTees);

      if (loadedHoles.length > 0 && loadedTees.length > 0) {
        const holeIds = loadedHoles.map((h) => h.id);
        const { data: distData } = await supabase.from('hole_tee_distances').select('*').in('hole_id', holeIds);
        setTeeDistances((distData as HoleTeeDistance[]) ?? []);
      }
    } catch {
      toast.error('Failed to load hole data');
    } finally {
      setLoading(false);
    }
  }, [course.id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const hole = holes.find((h) => h.hole_number === selectedHole);
    setForm(hole ? {
      par: hole.par,
      handicap_index: hole.handicap_index != null ? String(hole.handicap_index) : '',
      distance_meters: hole.distance_meters != null ? String(hole.distance_meters) : '',
      distance_yards: hole.distance_yards != null ? String(hole.distance_yards) : '',
      description: hole.description ?? '',
      notes: hole.notes ?? '',
    } : defaultHoleForm());

    if (hole && teeBoxes.length > 0) {
      const tf: TeeDistanceForm = {};
      teeBoxes.forEach((t) => {
        const dist = teeDistances.find((d) => d.hole_id === hole.id && d.tee_box_id === t.id);
        tf[t.id] = {
          yards: dist?.distance_yards != null ? String(dist.distance_yards) : '',
          meters: dist?.distance_meters != null ? String(dist.distance_meters) : '',
        };
      });
      setTeeForm(tf);
    } else {
      setTeeForm(Object.fromEntries(teeBoxes.map((t) => [t.id, { yards: '', meters: '' }])));
    }
    setDirty(false);
  }, [selectedHole, holes, teeBoxes, teeDistances]);

  const handleYardsChange = (val: string) => {
    setForm((prev) => ({
      ...prev,
      distance_yards: val,
      distance_meters: val && !isNaN(Number(val)) ? String(Math.round(Number(val) / 1.09361)) : prev.distance_meters,
    }));
    setDirty(true);
  };

  const handleMetersChange = (val: string) => {
    setForm((prev) => ({
      ...prev,
      distance_meters: val,
      distance_yards: val && !isNaN(Number(val)) ? String(Math.round(Number(val) * 1.09361)) : prev.distance_yards,
    }));
    setDirty(true);
  };

  const handleTeeYardsChange = (teeId: string, val: string) => {
    setTeeForm((prev) => ({
      ...prev,
      [teeId]: {
        yards: val,
        meters: val && !isNaN(Number(val)) ? String(Math.round(Number(val) / 1.09361)) : prev[teeId]?.meters ?? '',
      },
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const existing = holes.find((h) => h.hole_number === selectedHole);
      const payload = {
        course_id: course.id,
        hole_number: selectedHole,
        par: form.par,
        handicap_index: form.handicap_index ? Number(form.handicap_index) : null,
        distance_meters: form.distance_meters ? Number(form.distance_meters) : null,
        distance_yards: form.distance_yards ? Number(form.distance_yards) : null,
        description: form.description.trim() || null,
        notes: form.notes.trim() || null,
        updated_at: new Date().toISOString(),
      };

      let holeId: string;
      if (existing) {
        const { data, error } = await supabase.from('holes').update(payload).eq('id', existing.id).select().single();
        if (error) throw error;
        setHoles((prev) => prev.map((h) => (h.id === existing.id ? (data as Hole) : h)));
        holeId = existing.id;
      } else {
        const { data, error } = await supabase.from('holes').insert(payload).select().single();
        if (error) throw error;
        const newHole = data as Hole;
        setHoles((prev) => [...prev, newHole].sort((a, b) => a.hole_number - b.hole_number));
        holeId = newHole.id;
      }

      if (teeBoxes.length > 0) {
        const upserts = teeBoxes
          .filter((t) => teeForm[t.id]?.yards || teeForm[t.id]?.meters)
          .map((t) => ({
            hole_id: holeId,
            tee_box_id: t.id,
            distance_yards: teeForm[t.id]?.yards ? Number(teeForm[t.id].yards) : null,
            distance_meters: teeForm[t.id]?.meters ? Number(teeForm[t.id].meters) : null,
            updated_at: new Date().toISOString(),
          }));

        if (upserts.length > 0) {
          const { data: upserted, error: uErr } = await supabase
            .from('hole_tee_distances').upsert(upserts, { onConflict: 'hole_id,tee_box_id' }).select();
          if (uErr) throw uErr;
          const newDists = (upserted as HoleTeeDistance[]) ?? [];
          setTeeDistances((prev) => {
            const filtered = prev.filter((d) => !newDists.some((nd) => nd.hole_id === d.hole_id && nd.tee_box_id === d.tee_box_id));
            return [...filtered, ...newDists];
          });
        }

        const clearedIds = teeBoxes.filter((t) => !teeForm[t.id]?.yards && !teeForm[t.id]?.meters).map((t) => t.id);
        if (clearedIds.length > 0) {
          await supabase.from('hole_tee_distances').delete().eq('hole_id', holeId).in('tee_box_id', clearedIds);
          setTeeDistances((prev) => prev.filter((d) => !(d.hole_id === holeId && clearedIds.includes(d.tee_box_id))));
        }
      }

      toast.success(`Hole ${selectedHole} saved`);
      setDirty(false);
    } catch {
      toast.error('Failed to save hole');
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (dirty) await handleSave();
    if (selectedHole < totalHoles) setSelectedHole((n) => n + 1);
  };

  const handlePrev = async () => {
    if (dirty) await handleSave();
    if (selectedHole > 1) setSelectedHole((n) => n - 1);
  };

  const openNewTee = () => { setEditingTee(null); setTeeBoxForm(defaultTeeForm()); setTeeSheetOpen(true); };
  const openEditTee = (tee: TeeBox) => {
    setEditingTee(tee);
    setTeeBoxForm({
      name: tee.name,
      color: tee.color,
      player_categories: tee.player_categories.join(', '),
      course_rating: tee.course_rating != null ? String(tee.course_rating) : '',
      slope_rating: tee.slope_rating != null ? String(tee.slope_rating) : '',
    });
    setTeeSheetOpen(true);
  };

  const handleSaveTee = async () => {
    if (!teeBoxForm.name.trim()) { toast.error('Tee box name is required'); return; }
    setSavingTee(true);
    const cats = teeBoxForm.player_categories.split(',').map((s) => s.trim()).filter(Boolean);
    const payload = {
      course_id: course.id,
      name: teeBoxForm.name.trim(),
      color: teeBoxForm.color,
      player_categories: cats,
      course_rating: teeBoxForm.course_rating ? Number(teeBoxForm.course_rating) : null,
      slope_rating: teeBoxForm.slope_rating ? Number(teeBoxForm.slope_rating) : null,
      sort_order: editingTee ? editingTee.sort_order : teeBoxes.length,
      updated_at: new Date().toISOString(),
    };
    try {
      if (editingTee) {
        const { data, error } = await supabase.from('tee_boxes').update(payload).eq('id', editingTee.id).select().single();
        if (error) throw error;
        setTeeBoxes((prev) => prev.map((t) => (t.id === editingTee.id ? (data as TeeBox) : t)));
        toast.success('Tee box updated');
      } else {
        const { data, error } = await supabase.from('tee_boxes').insert(payload).select().single();
        if (error) throw error;
        setTeeBoxes((prev) => [...prev, data as TeeBox]);
        toast.success('Tee box added');
      }
      setTeeSheetOpen(false);
    } catch {
      toast.error('Failed to save tee box');
    } finally {
      setSavingTee(false);
    }
  };

  const handleDeleteTee = async (id: string) => {
    setDeletingTeeId(id);
    const { error } = await supabase.from('tee_boxes').delete().eq('id', id);
    if (error) { toast.error('Failed to delete tee box'); } else {
      setTeeBoxes((prev) => prev.filter((t) => t.id !== id));
      toast.success('Tee box removed');
    }
    setDeletingTeeId(null);
  };

  const completedCount = holes.length;
  const completionPct = Math.round((completedCount / totalHoles) * 100);
  const currentHoleData = holes.find((h) => h.hole_number === selectedHole);

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-golf-gray text-sm truncate">{course.name}</p>
          <p className="text-xs text-gray-500">Course Setup</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
          <p className="text-xs text-gray-500">Holes</p>
          <p className="font-bold text-golf-gray text-lg">{completedCount}<span className="text-xs text-gray-400 font-normal">/{totalHoles}</span></p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3 text-center">
          <p className="text-xs text-gray-500">Par</p>
          <p className="font-bold text-golf-gray text-lg">{holes.reduce((s, h) => s + h.par, 0) || course.par}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-xs text-gray-500 mb-1">Done</p>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-0.5">
            <div className="bg-golf-green h-1.5 rounded-full transition-all" style={{ width: `${completionPct}%` }} />
          </div>
          <p className="text-xs font-bold text-golf-gray">{completionPct}%</p>
        </div>
      </div>

      <div className="flex border-b border-gray-200 bg-white rounded-t-xl">
        {(['holes', 'tees'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-golf-orange text-golf-orange' : 'border-transparent text-gray-500'}`}
          >
            {tab === 'holes' ? 'Hole Layout' : `Tee Boxes${teeBoxes.length > 0 ? ` (${teeBoxes.length})` : ''}`}
          </button>
        ))}
      </div>

      {activeTab === 'tees' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">{teeBoxes.length} tee box{teeBoxes.length !== 1 ? 'es' : ''}</p>
            <button onClick={openNewTee} className="bg-golf-orange text-white rounded-xl px-3 py-2 flex items-center gap-1.5 text-xs font-medium">
              <PlusIcon className="w-3.5 h-3.5" />
              Add Tee
            </button>
          </div>
          {teeBoxes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-6 text-center">
              <p className="text-gray-400 text-sm mb-3">No tee boxes defined yet</p>
              <button onClick={openNewTee} className="text-golf-orange text-sm font-medium">Add first tee box</button>
            </div>
          ) : (
            <div className="space-y-2">
              {teeBoxes.map((tee) => (
                <div key={tee.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 ${COLOR_DOT[tee.color] ?? 'bg-gray-200'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-golf-gray text-sm">{tee.name}</p>
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                      {tee.player_categories.length > 0 && (
                        <span className="text-xs text-gray-500">{tee.player_categories.join(', ')}</span>
                      )}
                      {tee.course_rating && <span className="text-xs text-gray-400">CR {tee.course_rating}</span>}
                      {tee.slope_rating && <span className="text-xs text-gray-400">SR {tee.slope_rating}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEditTee(tee)} className="p-2 rounded-xl text-gray-400 hover:text-golf-orange hover:bg-golf-orange/5 transition-colors">
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDeleteTee(tee.id)} disabled={deletingTeeId === tee.id} className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'holes' && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
            </div>
          ) : (
            <>
              <div className="bg-white rounded-2xl border border-gray-100 p-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 px-1">Select Hole</p>
                <div className="grid grid-cols-9 gap-1">
                  {Array.from({ length: totalHoles }, (_, i) => {
                    const n = i + 1;
                    const hole = holes.find((h) => h.hole_number === n);
                    const parClass = hole ? (parColors[hole.par] ?? 'bg-gray-100 text-gray-600') : 'bg-gray-50 text-gray-300';
                    return (
                      <button
                        key={n}
                        onClick={() => setSelectedHole(n)}
                        className={`flex flex-col items-center py-1.5 rounded-lg border-2 transition-all ${selectedHole === n ? 'border-golf-orange bg-golf-orange/5' : 'border-transparent'}`}
                      >
                        <span className="text-[9px] text-gray-400 font-medium">{String(n).padStart(2, '0')}</span>
                        <span className={`mt-0.5 text-[9px] font-bold px-1 py-0.5 rounded-full ${parClass}`}>
                          {hole ? `P${hole.par}` : '—'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <button onClick={handlePrev} disabled={selectedHole === 1} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors">
                      <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
                    </button>
                    <div>
                      <p className="font-bold text-golf-gray">Hole {selectedHole}</p>
                      {currentHoleData && (
                        <p className="text-xs text-gray-500">
                          Par {currentHoleData.par}
                          {currentHoleData.distance_yards ? ` · ${currentHoleData.distance_yards}y` : ''}
                          {currentHoleData.handicap_index ? ` · HCP ${currentHoleData.handicap_index}` : ''}
                        </p>
                      )}
                    </div>
                    <button onClick={handleNext} disabled={selectedHole === totalHoles} className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30 transition-colors">
                      <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                  <button
                    onClick={handleSave}
                    disabled={saving || !dirty}
                    className="flex items-center gap-1.5 px-3 py-2 bg-golf-orange text-white text-xs font-semibold rounded-xl disabled:opacity-50 transition-colors"
                  >
                    {saving ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <CheckIcon className="w-3.5 h-3.5" />}
                    Save
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Par</label>
                    <div className="flex gap-2">
                      {[3, 4, 5, 6].map((p) => (
                        <button
                          key={p}
                          onClick={() => { setForm((prev) => ({ ...prev, par: p })); setDirty(true); }}
                          className={`flex-1 py-2.5 rounded-xl font-bold text-sm border-2 transition-all ${
                            form.par === p
                              ? p === 3 ? 'bg-blue-500 text-white border-blue-500'
                                : p === 4 ? 'bg-golf-green text-white border-golf-green'
                                : p === 5 ? 'bg-amber-500 text-white border-amber-500'
                                          : 'bg-red-500 text-white border-red-500'
                              : 'bg-white text-gray-600 border-gray-200'
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Handicap Index (1–18)</label>
                    <input
                      type="number"
                      value={form.handicap_index}
                      onChange={(e) => { setForm((prev) => ({ ...prev, handicap_index: e.target.value })); setDirty(true); }}
                      min={1} max={18}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                      placeholder="e.g. 1"
                    />
                  </div>

                  {teeBoxes.length === 0 ? (
                    <>
                      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 flex items-start gap-2">
                        <InformationCircleIcon className="w-4 h-4 text-golf-orange flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-medium text-gray-700">Set up tee boxes to enable per-tee distances</p>
                          <button onClick={() => setActiveTab('tees')} className="mt-1 text-xs font-semibold text-golf-orange">
                            Go to Tee Boxes tab →
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Yards</label>
                          <input
                            type="number"
                            value={form.distance_yards}
                            onChange={(e) => handleYardsChange(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                            placeholder="e.g. 425"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Metres</label>
                          <input
                            type="number"
                            value={form.distance_meters}
                            onChange={(e) => handleMetersChange(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                            placeholder="e.g. 389"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-2">Distances by Tee</label>
                      <div className="space-y-2">
                        {teeBoxes.map((tee) => (
                          <div key={tee.id} className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex-shrink-0 ${COLOR_DOT[tee.color] ?? 'bg-gray-200'}`} />
                            <span className="text-xs font-medium text-gray-700 w-20 truncate">{tee.name}</span>
                            <input
                              type="number"
                              value={teeForm[tee.id]?.yards ?? ''}
                              onChange={(e) => handleTeeYardsChange(tee.id, e.target.value)}
                              className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                              placeholder="yds"
                            />
                            <span className="text-xs text-gray-400">y</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => { setForm((prev) => ({ ...prev, description: e.target.value })); setDirty(true); }}
                      rows={2}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none resize-none"
                      placeholder="Describe the hole layout, hazards..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handlePrev}
                      disabled={selectedHole === 1}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl disabled:opacity-30 transition-colors"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                      Prev
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={selectedHole === totalHoles}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-xl disabled:opacity-30 transition-colors"
                    >
                      Next
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {completedCount < totalHoles && (
                <button
                  onClick={async () => {
                    for (let n = 1; n <= totalHoles; n++) {
                      if (!holes.find((h) => h.hole_number === n)) {
                        await supabase.from('holes').insert({ course_id: course.id, hole_number: n, par: 4, updated_at: new Date().toISOString() });
                      }
                    }
                    fetchAll();
                    toast.success('All holes initialized with par 4');
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-golf-green border border-golf-green/30 bg-golf-green/5 rounded-xl transition-colors"
                >
                  <PlusIcon className="w-4 h-4" />
                  Initialize All Holes (Par 4)
                </button>
              )}
            </>
          )}
        </div>
      )}

      {teeSheetOpen && (
        <div className="fixed inset-x-0 top-0 bottom-20 z-40 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setTeeSheetOpen(false)} />
          <div className="relative bg-white rounded-t-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
              <h3 className="font-bold text-golf-gray text-base">{editingTee ? 'Edit Tee Box' : 'Add Tee Box'}</h3>
              <button onClick={() => setTeeSheetOpen(false)} className="p-2 rounded-xl text-gray-400 hover:bg-gray-100">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tee Name *</label>
                <input
                  type="text"
                  value={teeBoxForm.name}
                  onChange={(e) => setTeeBoxForm({ ...teeBoxForm, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="e.g. Championship"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2">Color</label>
                <div className="flex flex-wrap gap-2">
                  {TEE_COLORS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setTeeBoxForm({ ...teeBoxForm, color: c.value })}
                      title={c.label}
                      className={`w-8 h-8 rounded-full transition-all ${c.bg} ${teeBoxForm.color === c.value ? 'ring-2 ring-offset-2 ring-golf-orange scale-110' : 'hover:scale-105'}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Player Categories (comma-separated)</label>
                <input
                  type="text"
                  value={teeBoxForm.player_categories}
                  onChange={(e) => setTeeBoxForm({ ...teeBoxForm, player_categories: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                  placeholder="e.g. Pro, Amateur A"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Course Rating</label>
                  <input
                    type="number"
                    value={teeBoxForm.course_rating}
                    onChange={(e) => setTeeBoxForm({ ...teeBoxForm, course_rating: e.target.value })}
                    step="0.1" min="55" max="80"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                    placeholder="72.4"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Slope Rating</label>
                  <input
                    type="number"
                    value={teeBoxForm.slope_rating}
                    onChange={(e) => setTeeBoxForm({ ...teeBoxForm, slope_rating: e.target.value })}
                    step="0.5" min="55" max="155"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange outline-none"
                    placeholder="113.0"
                  />
                </div>
              </div>
            </div>
            <div className="px-4 pb-6 flex gap-3">
              <button onClick={() => setTeeSheetOpen(false)} className="flex-1 py-3 rounded-xl text-sm font-medium text-gray-600 bg-gray-100">Cancel</button>
              <button onClick={handleSaveTee} disabled={savingTee} className="flex-1 py-3 rounded-xl text-sm font-semibold text-white bg-golf-orange disabled:opacity-60">
                {savingTee ? 'Saving...' : editingTee ? 'Save Changes' : 'Add Tee Box'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
